/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type CSSProperties, type FC, type ReactNode } from 'react';

import { useLoginI18n, type LoginStrings } from './i18n';

const footerLinkStyle: CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
};

const SpecBadge: FC<{ children: ReactNode }> = ({ children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    }}
  >
    <span
      style={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.5)',
      }}
    />
    {children}
  </span>
);

const FloatingCard: FC<{
  top: number;
  left: number;
  title: string;
  subtitle: string;
  tint: string;
  delay: number;
}> = ({ top, left, title, subtitle, tint, delay }) => (
  <div
    style={{
      position: 'absolute',
      top,
      left,
      width: 220,
      background: 'var(--hero-card-bg)',
      color: 'var(--text-1)',
      padding: '12px 14px',
      borderRadius: 10,
      boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
      animation: `trebox-login-float 6s ease-in-out ${delay}s infinite alternate`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: tint,
        }}
      />
      <span
        style={{
          fontSize: 11,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </span>
    </div>
    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600 }}>
      {subtitle}
    </div>
    <div
      style={{
        marginTop: 8,
        height: 4,
        background: 'var(--bg-elev)',
        borderRadius: 2,
      }}
    >
      <div
        style={{
          height: '100%',
          width: '60%',
          background: tint,
          borderRadius: 2,
        }}
      />
    </div>
  </div>
);

const DecorLayer: FC<{ t: LoginStrings }> = ({ t }) => (
  <>
    <svg
      style={{ position: 'absolute', inset: 0, opacity: 0.3 }}
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="trebox-login-dotgrid"
          width="24"
          height="24"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="1" cy="1" r="1" fill="white" opacity="0.25" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#trebox-login-dotgrid)" />
    </svg>

    <div
      style={{
        position: 'absolute',
        top: -120,
        right: -120,
        width: 480,
        height: 480,
        borderRadius: '50%',
        background: 'var(--hero-blob)',
        filter: 'blur(20px)',
      }}
    />

    <div
      style={{
        position: 'absolute',
        bottom: 80,
        right: -40,
        width: 360,
        height: 240,
        transform: 'rotate(-6deg)',
        zIndex: 1,
      }}
    >
      <FloatingCard
        top={0}
        left={20}
        title={t.card_chat}
        subtitle={t.card_chat_body}
        tint="#0064FA"
        delay={0}
      />
      <FloatingCard
        top={50}
        left={70}
        title={t.card_agent}
        subtitle={t.card_agent_body}
        tint="#1A9F4A"
        delay={0.3}
      />
      <FloatingCard
        top={100}
        left={120}
        title={t.card_skill}
        subtitle={t.card_skill_body}
        tint="#E27A00"
        delay={0.6}
      />
    </div>
  </>
);

const Wordmark: FC = () => (
  <div
    style={{
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.15)',
        backdropFilter: 'blur(8px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 700,
        fontSize: 18,
        border: '1px solid rgba(255,255,255,0.25)',
      }}
    >
      T
    </div>
    <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.2 }}>
      Trebox
    </span>
  </div>
);

const HeroContent: FC<{ t: LoginStrings }> = ({ t }) => (
  <div style={{ marginTop: 'auto', position: 'relative', zIndex: 2 }}>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.2)',
        fontSize: 12,
        fontWeight: 500,
        marginBottom: 24,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: '#7CE2A8',
        }}
      />
      {t.brand_badge}
    </div>
    <h1
      style={{
        margin: 0,
        fontSize: 'clamp(28px, 3.6vw, 44px)',
        lineHeight: 1.15,
        fontWeight: 700,
        letterSpacing: -1.2,
        maxWidth: 480,
      }}
    >
      {t.hero_line_1}
      <br />
      {t.hero_line_2}
      <br />
      <span
        style={{
          background: 'linear-gradient(90deg, #FFFFFF 0%, #BFD5FB 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {t.hero_line_3}
      </span>
    </h1>
    <p
      style={{
        marginTop: 18,
        fontSize: 15,
        opacity: 0.78,
        maxWidth: 440,
        lineHeight: 1.6,
      }}
    >
      {t.hero_subtitle}
    </p>

    <div
      style={{
        marginTop: 36,
        display: 'flex',
        gap: 28,
        fontSize: 12,
        opacity: 0.7,
      }}
    >
      <SpecBadge>SOC 2 Type II</SpecBadge>
      <SpecBadge>等保三级</SpecBadge>
      <SpecBadge>GDPR Ready</SpecBadge>
      <SpecBadge>ISO 27001</SpecBadge>
    </div>
  </div>
);

const HeroFooter: FC<{ t: LoginStrings }> = ({ t }) => (
  <div
    style={{
      position: 'absolute',
      bottom: 24,
      left: 56,
      right: 56,
      zIndex: 2,
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 12,
      opacity: 0.55,
    }}
  >
    <span>{t.footer_copy}</span>
    <span style={{ display: 'inline-flex', gap: 20 }}>
      <a style={footerLinkStyle}>{t.footer_privacy}</a>
      <a style={footerLinkStyle}>{t.footer_tos}</a>
      <a style={footerLinkStyle}>{t.footer_status}</a>
    </span>
  </div>
);

export const BrandPanel: FC = () => {
  const t = useLoginI18n();
  return (
    <aside
      className="trebox-login-root__brand-panel"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--hero-gradient)',
        color: 'white',
        padding: 'clamp(28px, 4vw, 40px) clamp(32px, 5vw, 56px)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <DecorLayer t={t} />
      <Wordmark />
      <HeroContent t={t} />
      <HeroFooter t={t} />
    </aside>
  );
};
